import { useState } from 'react';
import { X, Save, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { db } from '../lib/db';
import { formatCurrency } from '../lib/utils';
import type { Order, OrderItem, OrderStatus } from '../types';

interface OverrideEditorProps {
  order: Order | null;
  onClose: () => void;
  onSave: () => void;
}

interface ItemDraft {
  name: string;
  partNumber: string;
  quantity: number;
  unitPrice: number;
  listPrice: string;
  discountPercent: string;
  sellerName: string;
}

const EMPTY_ITEM: ItemDraft = {
  name: '',
  partNumber: '',
  quantity: 1,
  unitPrice: 0,
  listPrice: '',
  discountPercent: '',
  sellerName: '',
};

function toLocalInput(iso: string | undefined): string {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function itemToDraft(item: OrderItem): ItemDraft {
  return {
    name: item.name,
    partNumber: item.partNumber,
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    listPrice: item.listPrice != null ? String(item.listPrice) : '',
    discountPercent: item.discountPercent != null ? String(item.discountPercent) : '',
    sellerName: item.sellerName || '',
  };
}

export function OverrideEditor({ order, onClose, onSave }: OverrideEditorProps) {
  const isCreate = !order;

  const [form, setForm] = useState({
    customerName: order?.customerName || '',
    customerEmail: order?.customerEmail || '',
    poNumber: order?.poNumber || '',
    incrementId: order?.incrementId || '',
    orderDate: toLocalInput(order?.createdAt),
    status: (order?.status || 'unknown') as OrderStatus,
    notes: order?.notes || '',
    abandoned: order?.abandoned || false,
    paymentMethod: order?.paymentMethod || '',
    shippingMethodLabel: order?.shippingMethodLabel || '',
    shippingTotal: order?.shippingTotal != null ? String(order.shippingTotal) : '',
    taxTotal: order?.taxTotal != null ? String(order.taxTotal) : '',
    discountTotal: order?.discountTotal != null ? String(order.discountTotal) : '',
    manualTotal: order?.total != null ? String(order.total) : '',
  });
  const [items, setItems] = useState<ItemDraft[]>(
    order?.items?.length ? order.items.map(itemToDraft) : []
  );
  const [errors, setErrors] = useState<string[]>([]);

  const update = (field: keyof typeof form, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors([]);
  };

  const updateItem = (index: number, field: keyof ItemDraft, value: string | number) => {
    setItems(prev => prev.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
    setErrors([]);
  };

  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // Computed totals from line items
  const itemsSubtotal = items.reduce((sum, it) => sum + (Number(it.unitPrice) || 0) * (Number(it.quantity) || 1), 0);
  const shipping = Number(form.shippingTotal) || 0;
  const tax = Number(form.taxTotal) || 0;
  const discount = Number(form.discountTotal) || 0;
  const hasItems = items.length > 0;
  const grandTotal = hasItems
    ? Math.max(0, itemsSubtotal + shipping + tax - discount)
    : Number(form.manualTotal) || 0;

  const handleSave = () => {
    const errs: string[] = [];
    if (!form.customerName.trim()) errs.push('Customer name is required');
    if (!form.orderDate) errs.push('Order date is required');
    if (!hasItems && (Number(form.manualTotal) || 0) < 0) errs.push('Total cannot be negative');
    items.forEach((it, i) => {
      if (!it.partNumber.trim()) errs.push(`Item ${i + 1}: part number is required`);
      if (!it.name.trim()) errs.push(`Item ${i + 1}: item name is required`);
    });

    if (errs.length > 0) {
      setErrors(errs);
      return;
    }

    const isoDate = new Date(form.orderDate).toISOString();
    const mappedItems: OrderItem[] = items.map(it => ({
      partNumber: it.partNumber.trim(),
      name: it.name.trim(),
      quantity: Number(it.quantity) || 1,
      unitPrice: Number(it.unitPrice) || 0,
      listPrice: it.listPrice !== '' ? Number(it.listPrice) : undefined,
      discountPercent: it.discountPercent !== '' ? Number(it.discountPercent) : undefined,
      sellerName: it.sellerName.trim() || undefined,
    }));

    const payload = {
      customerName: form.customerName.trim(),
      customerEmail: form.customerEmail.trim(),
      poNumber: form.poNumber.trim() || undefined,
      incrementId: form.incrementId.trim() || undefined,
      total: grandTotal,
      subtotal: hasItems ? itemsSubtotal : undefined,
      shippingTotal: form.shippingTotal !== '' ? shipping : undefined,
      taxTotal: form.taxTotal !== '' ? tax : undefined,
      discountTotal: form.discountTotal !== '' ? discount : undefined,
      currency: 'USD',
      paymentMethod: form.paymentMethod.trim() || undefined,
      shippingMethodLabel: form.shippingMethodLabel.trim() || undefined,
      status: form.status,
      notes: form.notes,
      items: mappedItems,
      partNumbers: mappedItems.map(i => i.partNumber),
      sellerNames: [...new Set(mappedItems.map(i => i.sellerName).filter(Boolean))] as string[],
      abandoned: form.abandoned,
      createdAt: isoDate,
      sessionStart: isoDate,
    };

    if (isCreate) {
      db.createOrder(payload);
    } else {
      db.updateOrder(order.id, payload);
    }

    onSave();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 pb-4 px-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[calc(100vh-4rem)] overflow-y-auto bg-white rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-oem-200 bg-white rounded-t-2xl">
          <h2 className="font-display text-lg text-oem-navy">
            {isCreate ? 'Add Order' : order.source === 'live' ? 'Override Order' : 'Edit Order'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-oem-100 text-oem-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!isCreate && order.source === 'live' && (
            <div className="flex items-start gap-2 p-3 bg-status-intervention/10 border border-status-intervention/20 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-status-intervention mt-0.5 shrink-0" />
              <p className="text-sm text-status-intervention">
                This order comes from live analytics data. Your changes will be stored as overrides and take precedence over the original data.
              </p>
            </div>
          )}

          {errors.length > 0 && (
            <div className="p-3 bg-status-escalation/10 border border-status-escalation/20 rounded-lg space-y-1">
              {errors.map((err, i) => (
                <p key={i} className="text-sm text-status-escalation">{err}</p>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-oem-700">Customer Name</label>
              <input
                type="text"
                value={form.customerName}
                onChange={(e) => update('customerName', e.target.value)}
                placeholder="e.g. Acme Hospital"
                className="oem-input w-full"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-oem-700">Email / ID</label>
              <input
                type="text"
                value={form.customerEmail}
                onChange={(e) => update('customerEmail', e.target.value)}
                placeholder="e.g. ID 15"
                className="oem-input w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-oem-700">Purchase Order #</label>
              <input
                type="text"
                value={form.poNumber}
                onChange={(e) => update('poNumber', e.target.value)}
                placeholder="e.g. BB7056JC-Stock"
                className="oem-input w-full"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-oem-700">Order # (increment id)</label>
              <input
                type="text"
                value={form.incrementId}
                onChange={(e) => update('incrementId', e.target.value)}
                placeholder="e.g. 000000118"
                className="oem-input w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-oem-700">Order Date (local time)</label>
              <input
                type="datetime-local"
                value={form.orderDate}
                onChange={(e) => update('orderDate', e.target.value)}
                className="oem-input w-full"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-oem-700">Status</label>
              <select
                value={form.status}
                onChange={(e) => update('status', e.target.value as OrderStatus)}
                className="oem-input w-full"
              >
                <option value="unknown">Unknown</option>
                <option value="automation">Automation</option>
                <option value="intervention">Intervention</option>
                <option value="escalation">Escalation</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-oem-700">Payment Method</label>
              <input
                type="text"
                value={form.paymentMethod}
                onChange={(e) => update('paymentMethod', e.target.value)}
                placeholder="e.g. Purchase Order, Terms"
                className="oem-input w-full"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-oem-700">Shipping Method</label>
              <input
                type="text"
                value={form.shippingMethodLabel}
                onChange={(e) => update('shippingMethodLabel', e.target.value)}
                placeholder="e.g. FEDEX - GROUND"
                className="oem-input w-full"
              />
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-oem-700">Line Items ({items.length})</label>
              <button
                onClick={() => setItems(prev => [...prev, { ...EMPTY_ITEM }])}
                className="oem-button-outline flex items-center gap-1.5 text-xs py-1.5 px-3"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Item
              </button>
            </div>

            {items.map((item, i) => (
              <div key={i} className="border border-oem-200 rounded-lg p-3 space-y-2 bg-oem-50/50">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-oem-muted uppercase tracking-wider">Item {i + 1}</span>
                  <button
                    onClick={() => removeItem(i)}
                    className="p-1 rounded hover:bg-red-50 text-oem-400 hover:text-red-600 transition-colors"
                    title="Remove item"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateItem(i, 'name', e.target.value)}
                  placeholder="Item name"
                  className="oem-input w-full"
                />
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={item.partNumber}
                    onChange={(e) => updateItem(i, 'partNumber', e.target.value)}
                    placeholder="Part #"
                    className="oem-input w-full font-mono"
                  />
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(i, 'quantity', Number(e.target.value))}
                    placeholder="Qty"
                    className="oem-input w-full"
                  />
                  <input
                    type="text"
                    value={item.sellerName}
                    onChange={(e) => updateItem(i, 'sellerName', e.target.value)}
                    placeholder="Seller"
                    className="oem-input w-full"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-oem-muted">Unit price</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(i, 'unitPrice', Number(e.target.value))}
                      className="oem-input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-oem-muted">List price</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.listPrice}
                      onChange={(e) => updateItem(i, 'listPrice', e.target.value)}
                      className="oem-input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-oem-muted">Discount %</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={item.discountPercent}
                      onChange={(e) => updateItem(i, 'discountPercent', e.target.value)}
                      className="oem-input w-full"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-oem-700">Totals</label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-oem-muted">Shipping</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.shippingTotal}
                  onChange={(e) => update('shippingTotal', e.target.value)}
                  className="oem-input w-full"
                />
              </div>
              <div>
                <label className="text-xs text-oem-muted">Tax</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.taxTotal}
                  onChange={(e) => update('taxTotal', e.target.value)}
                  className="oem-input w-full"
                />
              </div>
              <div>
                <label className="text-xs text-oem-muted">Discount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.discountTotal}
                  onChange={(e) => update('discountTotal', e.target.value)}
                  className="oem-input w-full"
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-oem-100 rounded-lg">
              {hasItems ? (
                <>
                  <span className="text-sm text-oem-muted">
                    {formatCurrency(itemsSubtotal)} items + {formatCurrency(shipping)} shipping + {formatCurrency(tax)} tax − {formatCurrency(discount)} discount
                  </span>
                  <span className="font-display text-xl text-oem-navy">{formatCurrency(grandTotal)}</span>
                </>
              ) : (
                <>
                  <span className="text-sm text-oem-muted">Order total (no line items)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.manualTotal}
                    onChange={(e) => update('manualTotal', e.target.value)}
                    className="oem-input w-36 text-right font-semibold"
                    placeholder="0.00"
                  />
                </>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-oem-700">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              rows={3}
              placeholder="Additional references, tracking numbers, seller order IDs…"
              className="oem-input w-full resize-none"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="abandoned"
              checked={form.abandoned}
              onChange={(e) => update('abandoned', e.target.checked)}
              className="rounded border-oem-300 text-accent-600 focus:ring-accent-500"
            />
            <label htmlFor="abandoned" className="text-sm text-oem-700">
              Mark as abandoned cart
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-oem-200">
            <button onClick={onClose} className="oem-button-outline">
              Cancel
            </button>
            <button onClick={handleSave} className="oem-button flex items-center gap-2">
              <Save className="w-4 h-4" />
              {isCreate ? 'Create Order' : 'Save Override'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
